import { useState } from 'react';
import { MoreVertical, ChevronLeft, ChevronRight, CalendarIcon, X } from 'lucide-react';
import { ConfirmModal } from './core/Modal';
import { useUserRequests } from '../graphql/hooks/useUserDashboard';
import { useAuth } from '../graphql/hooks/useAuth';
import { useDeleteTracking } from '../graphql/hooks/useAdminDashboard';
import type { UserRequestItem } from '../graphql/types/timeoff';
import { useUpdateTracking, useActiveUsers } from '../graphql/hooks/useUserDashboard';
import toast from "react-hot-toast";


  const toastSuccess = (message: string) =>
    toast.success(message, {
      duration: 3000,
      style: {
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        color: '#15803d', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#16a34a', secondary: '#f0fdf4' },
    });

  const toastError = (message: string) =>
    toast.error(message, {
      duration: 4000,
      style: {
        background: '#fef2f2', border: '1px solid #fecaca',
        color: '#dc2626', padding: '12px 14px',
        borderRadius: '8px', fontSize: '14px',
      },
      iconTheme: { primary: '#dc2626', secondary: '#fef2f2' },
    });

  const formatInputDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-');
    return `${day}.${month}.${year}`;
  };

  const countWeekdays = (from: Date, to: Date) => {
    let count = 0;
    const cur = new Date(from);
    cur.setHours(0, 0, 0, 0);
    const end = new Date(to);
    end.setHours(0, 0, 0, 0);
    while (cur <= end) {
      const d = cur.getDay();
      if (d !== 0 && d !== 6) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };


type EditModalProps = {
  entry: UserRequestItem;
  onClose: () => void;
};

export function EditRequestModal({ entry, onClose }: EditModalProps) {
  const { mutate: updateTracking, isPending } = useUpdateTracking();
  const { data: activeUsers } = useActiveUsers();
  const { userType } = useAuth();
  const isAdmin = userType === 'admin' || userType === 'Superadmin';


  const monthNames = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
  const dayNames = ['Mo','Di','Mi','Do','Fr','Sa','So'];

  const parseLocalDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const [currentMonth, setCurrentMonth] = useState(() => parseLocalDate(entry.start_date));
  const [vacationRange, setVacationRange] = useState<{ from: Date | null; to: Date | null }>({
    from: parseLocalDate(entry.start_date),
    to: parseLocalDate(entry.end_date),
  });

  const [showCalendar, setShowCalendar] = useState(false);
  const [substituteSearch, setSubstituteSearch] = useState(entry.substitute?.full_name ?? '');
  const [selectedSubstitute, setSelectedSubstitute] = useState<{ id: string; full_name: string } | null>(
    entry.substitute?.id && entry.substitute?.full_name
      ? { id: entry.substitute.id, full_name: entry.substitute.full_name }
      : null,
  );
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');

  const filteredUsers =
    substituteSearch === ''
      ? []
      : (activeUsers ?? []).filter((u) =>
          u.full_name.toLowerCase().includes(substituteSearch.toLowerCase()),
        );

  const getDaysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (d: Date) => {
    const fd = new Date(d.getFullYear(), d.getMonth(), 1).getDay();
    return fd === 0 ? 6 : fd - 1;
  };

  const isInRange = (date: Date) =>
    vacationRange.from && vacationRange.to &&
    date >= vacationRange.from && date <= vacationRange.to;

  const isRangeEdge = (date: Date) =>
    (vacationRange.from && date.toDateString() === vacationRange.from.toDateString()) ||
    (vacationRange.to && date.toDateString() === vacationRange.to.toDateString());

  const handleDateClick = (date: Date) => {
    if (!vacationRange.from || (vacationRange.from && vacationRange.to)) {
      setVacationRange({ from: date, to: null });
      return;
    }
    const from = date < vacationRange.from ? date : vacationRange.from;
    const to = date < vacationRange.from ? vacationRange.from : date;
    setVacationRange({ from, to });
    setShowCalendar(false);
  };

  const toLocalIso = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const displayRange =
    vacationRange.from && vacationRange.to
      ? `${formatInputDate(toLocalIso(vacationRange.from))} – ${formatInputDate(toLocalIso(vacationRange.to))}`
      : vacationRange.from
      ? `${formatInputDate(toLocalIso(vacationRange.from))} – …`
      : 'Datum auswählen';

  const days =
    vacationRange.from && vacationRange.to
      ? countWeekdays(vacationRange.from, vacationRange.to)
      : 0;

  const handleSave = () => {
    if (!vacationRange.from || !vacationRange.to) {
      toastError('Bitte Zeitraum auswählen');
      return;
    }

    if (!isAdmin && !reason.trim()) {
      toastError('Bitte geben Sie einen Grund der Änderung an');
      return;
    }

    const start = toLocalIso(vacationRange.from);
    const end = toLocalIso(vacationRange.to);

    updateTracking(
      {
        id: entry.id,
        type: entry.type === 'holiday' ? 'HOLIDAY' : 'OVERTIME',
        start_date: start,
        end_date: end,
        substitute_id: selectedSubstitute?.id ?? null,
        comment: comment || null,
        reason: reason || null,
      },
      {
        onSuccess: () => {
          toastSuccess(
            entry.type === 'holiday'
              ? `Urlaub aktualisiert • ${displayRange}`
              : `Überstunden aktualisiert • ${displayRange}`,
          );
          onClose();
        },
        onError: () => toastError('Eintrag konnte nicht aktualisiert werden'),
      },
    );
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-[12px] shadow-xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9e9e9]">
          <h2 className="text-[#1c1c1c] text-[18px] font-medium">
            {entry.type === 'holiday' ? 'Urlaub bearbeiten' : 'Überstunden bearbeiten'}
          </h2>
          <button
            onClick={onClose}
            className="text-[#6b6b6b] hover:text-[#1c1c1c] text-[20px] leading-none"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Date Range Picker */}
          <div className="relative">
            <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
              {entry.type === 'holiday' ? 'Urlaub von – bis:' : 'Überstunden von – bis:'}
            </label>
            <input
              type="text"
              readOnly
              value={displayRange}
              onClick={() => setShowCalendar((p) => !p)}
              className="w-full box-border h-[44px] px-4 pr-10 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0084c7]"
            />
            <CalendarIcon className="absolute right-3 top-[38px] size-5 text-[#6b6b6b] pointer-events-none" />

            {showCalendar && (
              <div className="absolute z-30 mt-2 w-full box-border bg-white border border-[#e9e9e9] rounded-[12px] shadow-lg p-4">
                {/* Calendar header */}
                <div className="flex items-center justify-between mb-4">
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                    <ChevronLeft className="size-5 text-[#6b6b6b]" />
                  </button>
                  <span className="text-[14px] font-medium">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </span>
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                    <ChevronRight className="size-5 text-[#6b6b6b]" />
                  </button>
                </div>
                {/* Weekday headers */}
                <div className="grid grid-cols-7 text-center text-[12px] text-[#6b6b6b] mb-2">
                  {dayNames.map((d) => <div key={d}>{d}</div>)}
                </div>
                {/* Days */}
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDay }).map((_, i) => <div key={i} />)}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i + 1);
                    return (
                      <button
                        key={i}
                        onClick={() => handleDateClick(date)}
                        className={`h-9 rounded-md text-[13px] transition-colors ${
                          isRangeEdge(date)
                            ? 'bg-[#0084c7] text-white'
                            : isInRange(date)
                            ? 'bg-[#0084c7]/20 text-[#0084c7]'
                            : 'hover:bg-[#f5f5f5]'
                        }`}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Days count */}
          <div>
            <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
              {entry.type === 'holiday' ? 'Urlaubstage:' : 'Überstundentage:'}
            </label>
            <div className="h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-[#f5f5f5] text-[#1c1c1c] flex items-center text-[14px]">
              {days} Tage
            </div>
          </div>

          {/* Substitute */}
          <div className="relative">
            <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
              Notdienstvertretung:
            </label>
            <input
              type="text"
              value={substituteSearch}
              onChange={(e) => {
                setSubstituteSearch(e.target.value);
                setSelectedSubstitute(null);
              }}
              placeholder="Name eingeben…"
              className="w-full box-border h-[44px] px-4 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#0084c7]"
            />
            {substituteSearch && !selectedSubstitute && (
              <div className="absolute z-20 mt-1 w-full border border-[#e9e9e9] rounded-[8px] bg-white max-h-[200px] overflow-y-auto shadow-sm">
                {filteredUsers.length === 0 ? (
                  <div className="p-3 text-[#6b6b6b] text-[14px]">Keine Ergebnisse</div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => {
                        setSubstituteSearch(user.full_name);
                        setSelectedSubstitute(user);
                      }}
                      className="px-4 py-2 text-[14px] cursor-pointer hover:bg-blue-50"
                    >
                      {user.full_name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
              Kommentar (optional):
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Kommentar eingeben…"
              className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] resize-none text-[14px]"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-[#1c1c1c] text-[14px] font-medium mb-2">
              Grund der Änderung{' '}
              {isAdmin
                ? <span className="text-[#9ca3af] font-normal">(optional)</span>
                : <span className="text-red-500">*</span>
              }
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="z. B. Terminkollision…"
              className="w-full box-border px-4 py-3 border border-[#e9e9e9] rounded-[8px] bg-white text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] resize-none text-[14px]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e9e9e9]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[14px] text-[#6b6b6b] hover:text-[#1c1c1c] transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-5 py-2 bg-[#0084c7] text-white text-[14px] rounded-[8px] hover:bg-[#0073b1] transition-colors disabled:opacity-50"
          >
            {isPending ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UserRequestsView() {
  const [approvedPage, setApprovedPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingItemsPerPage, setPendingItemsPerPage] = useState(10);
  const [approvedItemsPerPage, setApprovedItemsPerPage] = useState(10);
  const [editModalEntry, setEditModalEntry] = useState<UserRequestItem | null>(null);
  const { data: activeUsers } = useActiveUsers();
  const { userType } = useAuth();
  const isAdmin = userType === 'admin' || userType === 'Superadmin';

  const { data } = useUserRequests({
    approvedPage,
    approvedLimit: approvedItemsPerPage,
    pendingPage,
    pendingLimit: pendingItemsPerPage,
  });

  const approvedRequests = data?.getUserRequests.approved.data ?? [];
  const pendingRequests = data?.getUserRequests.pending.data ?? [];
  const approvedPaginator = data?.getUserRequests.approved.paginatorInfo;
  const pendingPaginator = data?.getUserRequests.pending.paginatorInfo;

  const { userName } = useAuth()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [requestToDelete, setRequestToDelete] = useState<UserRequestItem | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const { mutate: deleteRequest } = useDeleteTracking();

  const parseEditPayload = (payload?: string | null) => {
    if (!payload) return null;
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  };

  const formatDateShort = (isoDate?: string | null) => {
    if (!isoDate) return null;
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('de-DE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleDeleteClick = (request: UserRequestItem) => {
    setRequestToDelete(request);
    setDeleteReason('');
    setDeleteModalOpen(true);
    setOpenDropdownId(null);
  };

  const handleEditClick = (request: UserRequestItem) => {
    setEditModalEntry(request);
    setOpenDropdownId(null);
  };


  const getRequestTypeLabel = (type?: string) => {
    switch (type) {
      case 'holiday':
        return 'Urlaub';
      case 'sick':
        return 'Krank';
      case 'overtime':
        return 'Überstunden';
      default:
        return '-';
    }
  };

  const mapTrackingTypeToEnum = (
    type: string
  ): "HOLIDAY" | "OVERTIME" | "SICK" | "TIME" => {
    switch (type) {
      case "holiday":
        return "HOLIDAY";
      case "overtime":
        return "OVERTIME";
      case "sick":
        return "SICK";
      case "time":
        return "TIME";
      default:
        throw new Error(`Unknown tracking type: ${type}`);
    }
  };

  const isRequestInPast = (endDate: string) => {
    const [year, month, day] = endDate.split('-').map(Number);
    const end = new Date(year, month - 1, day);
    end.setHours(23, 59, 59, 999);
    return end < new Date();
  };

  const renderDropdown = (entry: UserRequestItem) => {
    const isPast = isRequestInPast(entry.end_date);

    if (isPast) return null;  

    return (
      <div className="absolute right-0 top-8 bg-white border border-[#e9e9e9] rounded-[6px] shadow-lg z-10 min-w-[140px]">
        {(entry.type === 'holiday' || entry.type === 'overtime') && (
          <button
            onClick={() => handleEditClick(entry)}
            className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors"
          >
            Bearbeiten
          </button>
        )}
        <button
          onClick={() => handleDeleteClick(entry)}
          className="w-full px-4 py-2.5 text-left text-[14px] text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors"
        >
          Löschen
        </button>
      </div>
    );
  };;

  const formatLocalDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('de-DE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatLocalDateShort = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('de-DE');
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-[#1c1c1c] text-[24px] md:text-[28px] font-medium mb-1">
            Meine Anträge
          </h1>
          <p className="text-[#6b6b6b] text-[14px] md:text-[16px]">
            Übersicht über Ihre Urlaubs- und Überstundenanträge
          </p>
          <p className="text-[#6b6b6b] text-[13px] mt-2">
            Heute ist der {new Date().toLocaleDateString('de-DE', { 
              weekday: 'long', 
              day: '2-digit', 
              month: 'long', 
              year: 'numeric' 
            })}
          </p>
        </div>
      <div className="space-y-6">


      {/* Data Display */}
        <div className="space-y-6 bg-white rounded-[10px] border border-[#e9e9e9] p-6">
          {/* Offene Anträge Section */}
          <div className="space-y-4">
            <h2 className="text-[#1c1c1c] text-[18px] font-medium">
              Offene Anträge
            </h2>
           <div className="hidden md:flex items-center gap-2 text-[#6b6b6b] text-[14px]">
              <span>Zeige</span>

              <select
                value={pendingItemsPerPage}
                onChange={(e) => {
                  setPendingItemsPerPage(Number(e.target.value));
                  setPendingPage(1);
                }}
                className="form-select px-2 py-1 border border-[#e0e0e0] rounded"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>

              <span>Einträge</span>
            </div>  
            {/* Desktop Header */}
            <div className="hidden md:grid grid-cols-[1.2fr_0.8fr_2fr_1fr_1fr] gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
              <div>Antragsteller</div>
              <div>Typ</div>
              <div>Zeitraum</div>
              <div>Vertretung</div>
              <div>Erstellt / Geändert</div>
            </div>

            {/* Cards for Open Requests */}
            {pendingRequests?.length ? (
              pendingRequests.map((entry) => {
                const editPayload = parseEditPayload(entry.edit_payload);
                const userList = activeUsers ?? [];
                const substituteName = editPayload?.substitute_id !== undefined
                  ? userList.find(u => String(u.id) === String(editPayload.substitute_id))?.full_name ?? editPayload.substitute_id
                  : null;

    return (
      <>
        <div
          key={entry.id}
          className={`hidden md:grid grid-cols-[1.2fr_0.8fr_2fr_1fr_1fr] gap-4 px-4 py-3 text-[16px] items-start hover:bg-[#f5f5f5] transition-colors ${
            entry.request_type === 1 ? 'border-l-4 border-red-500'
            : entry.request_type === 2 ? 'border-l-4 border-blue-500'
            : 'border-l-4 border-transparent'
          }`}
        >
          <div className="text-[#1c1c1c] font-medium">{userName}</div>
          <div className="text-[#6b6b6b]">{getRequestTypeLabel(entry.type)}</div>
          <div className="text-[#6b6b6b] space-y-1">
            <div>{formatLocalDate(entry.start_date)} bis {formatLocalDate(entry.end_date)}</div>
            {editPayload?.start_date || editPayload?.end_date ? (
              <div className="text-blue-500 text-[13px]">
                → {formatDateShort(editPayload.start_date)} bis {formatDateShort(editPayload.end_date)}
              </div>
            ) : null}
          </div>
          <div className="text-[#6b6b6b] flex justify-between items-start">
            <div className="space-y-1">
              <div>{entry.substitute?.full_name ?? 'Kein Notdienst'}</div>
              {editPayload && editPayload.substitute_id !== undefined && (
                <div className="text-blue-500 text-[13px]">
                  → {substituteName ?? 'Kein Notdienst'}
                </div>
              )}
            </div>
            {!isRequestInPast(entry.end_date) && (
              <div className="relative">
                <button
                  onClick={() => setOpenDropdownId(openDropdownId === entry.id ? null : entry.id)}
                  className="p-1 hover:bg-[#f5f5f5] rounded-[6px] transition-colors"
                >
                  <MoreVertical className="size-5 text-[#6b6b6b]" />
                </button>
                {openDropdownId === entry.id && renderDropdown(entry)}
              </div>
            )}
          </div>
          {/* Eingereicht / Geändert */}
          <div className="text-[#6b6b6b] text-[14px] space-y-1">
            <div>{entry.created_at ? new Date(entry.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</div>
            {entry.updated_at && entry.updated_at !== entry.created_at && (
              <div className="text-[12px] text-[#9ca3af]">
                Geändert: {new Date(entry.updated_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        {/* Comment/Reason row */}
        {editPayload && (editPayload.comment || editPayload.reason) && (
          <div className="hidden md:flex px-4 pb-3 gap-x-6 text-[13px]">
            {editPayload.comment && (
              <div>
                <span className="text-[#6b6b6b]">Kommentar: </span>
                <span className="text-blue-600">{editPayload.comment}</span>
              </div>
            )}
            {editPayload.reason && (
              <div>
                <span className="text-[#6b6b6b]">Grund: </span>
                <span className="text-blue-600">{editPayload.reason}</span>
              </div>
            )}
          </div>
        )}

        {/* Mobile Card */}
        <div className="md:hidden bg-white border border-[#e9e9e9] rounded-[10px] p-4 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[15px] font-medium text-[#1c1c1c]">{userName}</p>
              <p className="text-[13px] text-[#6b6b6b]">{getRequestTypeLabel(entry.type)}</p>
            </div>
            {!isRequestInPast(entry.end_date) && (
              <div className="relative">
                <button
                  onClick={() => setOpenDropdownId(openDropdownId === entry.id ? null : entry.id)}
                  className="p-1 hover:bg-[#f5f5f5] rounded-[6px] transition-colors"
                >
                  <MoreVertical className="size-5 text-[#6b6b6b]" />
                </button>
                {openDropdownId === entry.id && renderDropdown(entry)}
              </div>
            )}
          </div>
          <div className="text-[14px] text-[#6b6b6b]">
            📅 {formatLocalDateShort(entry.start_date)} — {formatLocalDateShort(entry.end_date)}
            {(editPayload?.start_date || editPayload?.end_date) && (
              <div className="text-blue-500 text-[13px] mt-0.5">
                → {formatDateShort(editPayload.start_date)} – {formatDateShort(editPayload.end_date)}
              </div>
            )}
          </div>
          <div className="text-[14px] text-[#6b6b6b]">
            👤 {entry.substitute?.full_name ?? 'Kein Notdienst'}
            {editPayload && editPayload.substitute_id !== undefined && (
              <div className="text-blue-500 text-[13px] mt-0.5">→ {substituteName ?? 'Kein Notdienst'}</div>
            )}
          </div>
          {editPayload && (editPayload.comment || editPayload.reason) && (
            <div className="text-[13px] space-y-1 pt-1 border-t border-[#f0f0f0]">
              {editPayload.comment && (
                <div><span className="text-[#6b6b6b]">Kommentar: </span><span className="text-blue-600">{editPayload.comment}</span></div>
              )}
              {editPayload.reason && (
                <div><span className="text-[#6b6b6b]">Grund: </span><span className="text-blue-600">{editPayload.reason}</span></div>
              )}
            </div>
          )}
        </div>
      </>
    );
  })
) : (
  <div className="px-4 py-6 text-[#6b6b6b] text-[14px] text-center border border-[#e0e0e0] rounded-lg bg-white">
    Keine Daten vorhanden
  </div>
)}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4 border-t border-[#e0e0e0]">
                {/* Desktop "showing x to y" */}
                <div className="hidden md:block text-[#6b6b6b] text-[14px]">
                  Zeige{" "}
                  {Math.min(
                    ((pendingPaginator?.currentPage ?? 1) - 1) * pendingItemsPerPage + 1,
                    pendingPaginator?.total ?? 0
                  )}{" "}
                  bis{" "}
                  {Math.min(
                    (pendingPaginator?.currentPage ?? 1) * pendingItemsPerPage,
                    pendingPaginator?.total ?? 0
                  )}{" "}
                  von {pendingPaginator?.total ?? 0} Einträgen
                </div>

                {/* Mobile: select + pagination in one line */}
                <div className="flex items-center justify-between gap-3 md:justify-end">
                  <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px] md:hidden whitespace-nowrap">
                    <span>Zeige</span>
                    <select
                      value={pendingItemsPerPage}
                      onChange={(e) => {
                        setPendingItemsPerPage(Number(e.target.value));
                        setPendingPage(1);
                      }}
                      className="px-2 py-1 border border-[#e0e0e0] rounded bg-white text-[#1c1c1c] text-[14px]"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Prev */}
                    <button
                      onClick={() => setPendingPage((prev) => Math.max(1, prev - 1))}
                      disabled={pendingPaginator?.currentPage === 1}
                      className="p-2 rounded hover:bg-[#f5f5f5] disabled:opacity-50"
                      aria-label="Vorherige Seite"
                    >
                      <ChevronLeft className="size-5 text-[#6b6b6b]" />
                    </button>

                    {/* Page numbers (keep for desktop, optional for mobile) */}
                    <div className="hidden md:flex items-center gap-2">
                      {Array.from({ length: pendingPaginator?.lastPage ?? 1 }, (_, i) => i + 1)
                        .slice(
                          Math.max(0, (pendingPaginator?.currentPage ?? 1) - 3),
                          (pendingPaginator?.currentPage ?? 1) + 2
                        )
                        .map((page) => (
                          <button
                            key={page}
                            onClick={() => setPendingPage(page)}
                            className={`px-3 py-1 rounded ${
                              pendingPaginator?.currentPage === page
                                ? "bg-[#0084c7] text-white"
                                : "text-[#6b6b6b] hover:bg-[#f5f5f5]"
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                    </div>
                    <div className="md:hidden text-[13px] text-[#6b6b6b] min-w-[52px] text-center">
                      {(pendingPaginator?.currentPage ?? 1)} / {(pendingPaginator?.lastPage ?? 1)}
                    </div>
                    {/* Next */}
                    <button
                      onClick={() =>
                        setPendingPage((prev) =>
                          Math.min(pendingPaginator?.lastPage ?? prev, prev + 1)
                        )
                      }
                      disabled={pendingPaginator?.currentPage === pendingPaginator?.lastPage}
                      className="p-2 rounded hover:bg-[#f5f5f5] disabled:opacity-50"
                      aria-label="Nächste Seite"
                    >
                      <ChevronRight className="size-5 text-[#6b6b6b]" />
                    </button>
                  </div>
                </div>
              </div>
          </div>

          {/* Frühere Anträge Section */}
          <div className="space-y-4">
            <h2 className="text-[#1c1c1c] text-[18px] font-medium">
              Frühere Anträge
            </h2>
            <div className="hidden md:flex items-center gap-2 text-[#6b6b6b] text-[14px]">
              <span>Zeige</span>

              <select
                value={approvedItemsPerPage}
                onChange={(e) => {
                  setApprovedItemsPerPage(Number(e.target.value));
                  setApprovedPage(1);
                }}
                className="form-select px-2 py-1 border border-[#e0e0e0] rounded"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>

              <span>Einträge</span>
            </div>     
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-[1.2fr_0.8fr_2fr_1fr_1fr] gap-4 px-4 py-3 bg-[#f5f5f5] rounded-lg text-[14px] font-medium text-[#6b6b6b]">
              <div>Antragsteller</div>
              <div>Typ</div>
              <div>Zeitraum</div>
              <div>Vertretung</div>
              <div>Erstellt / Geändert</div>
            </div>

            {/* Table Rows */}
            {approvedRequests?.length ? (
              approvedRequests.map((entry) => (
                <>
                  <div
                    key={entry.id}
                    className="hidden md:grid grid-cols-[1.2fr_0.8fr_2fr_1fr_1fr] gap-4 px-4 py-3 text-[16px] items-center hover:bg-[#f5f5f5] transition-colors"
                  >
                    <div className="text-[#1c1c1c]">{userName}</div>
                    <div className="text-[#6b6b6b]">{getRequestTypeLabel(entry.type)}</div>
                    <div className="text-[#6b6b6b]">
                      {formatLocalDate(entry.start_date)} bis {formatLocalDate(entry.end_date)}
                    </div>
                    <div className="text-[#6b6b6b] flex justify-between items-center">
                      <span>{entry.substitute?.full_name ?? 'Kein Notdienst'}</span>
                      {!isRequestInPast(entry.end_date) && (
                        <div className="relative">
                          <button
                            onClick={() => setOpenDropdownId(openDropdownId === entry.id ? null : entry.id)}
                            className="p-1 hover:bg-[#f5f5f5] rounded-[6px] transition-colors"
                          >
                            <MoreVertical className="size-5 text-[#6b6b6b]" />
                          </button>
                          {openDropdownId === entry.id && renderDropdown(entry)}
                        </div>
                      )}
                    </div>
                    <div className="text-[#6b6b6b] text-[14px] space-y-1">
                      <div>{entry.created_at ? new Date(entry.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</div>
                      {entry.updated_at && entry.updated_at !== entry.created_at && (
                        <div className="text-[12px] text-[#9ca3af]">
                          Geändert: {new Date(entry.updated_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                {/* Mobile Card */}
                <div className="md:hidden bg-white border border-[#e9e9e9] rounded-[10px] p-4 space-y-3">

                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[15px] font-medium text-[#1c1c1c]">
                        {userName}
                      </p>
                      <p className="text-[13px] text-[#6b6b6b]">
                        {getRequestTypeLabel(entry.type)}
                      </p>
                    </div>

                      {!isRequestInPast(entry.end_date) && (
                        <div className="relative">
                          <button
                            onClick={() => setOpenDropdownId(openDropdownId === entry.id ? null : entry.id)}
                            className="p-1 hover:bg-[#f5f5f5] rounded-[6px] transition-colors"
                          >
                            <MoreVertical className="size-5 text-[#6b6b6b]" />
                          </button>
                          {openDropdownId === entry.id && renderDropdown(entry)}
                        </div>
                      )}
                  </div>

                  <div className="text-[14px] text-[#6b6b6b]">
                    📅 {formatLocalDateShort(entry.start_date)} — {formatLocalDateShort(entry.end_date)}
                  </div>

                  <div className="text-[14px] text-[#6b6b6b]">
                    👤 {entry.substitute?.full_name ?? "Kein Notdienst"}
                  </div>

                </div>
                </>
              ))
            ) : (
              <div className="px-4 py-6 text-[#6b6b6b] text-[14px] text-center border border-[#e0e0e0] rounded-lg bg-white">
                Keine Daten vorhanden
              </div>
            )}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4 border-t border-[#e0e0e0]">
              {/* Desktop info text */}
              <div className="hidden md:block text-[#6b6b6b] text-[14px]">
                Zeige{" "}
                {Math.min(
                  ((approvedPaginator?.currentPage ?? 1) - 1) *
                    approvedItemsPerPage +
                    1,
                  approvedPaginator?.total ?? 0
                )}{" "}
                bis{" "}
                {Math.min(
                  (approvedPaginator?.currentPage ?? 1) *
                    approvedItemsPerPage,
                  approvedPaginator?.total ?? 0
                )}{" "}
                von {approvedPaginator?.total ?? 0} Einträgen
              </div>

              {/* Mobile: select + pagination */}
              <div className="flex items-center justify-between gap-3 md:justify-end">
                {/* Mobile select */}
                <div className="flex items-center gap-2 text-[#6b6b6b] text-[14px] md:hidden whitespace-nowrap">
                  <span>Zeige</span>
                  <select
                    value={approvedItemsPerPage}
                    onChange={(e) => {
                      setApprovedItemsPerPage(Number(e.target.value));
                      setApprovedPage(1);
                    }}
                    className="px-2 py-1 border border-[#e0e0e0] rounded bg-white text-[#1c1c1c] text-[14px]"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* Pagination */}
                <div className="flex items-center gap-2">
                  {/* Prev Arrow */}
                  <button
                    onClick={() =>
                      setApprovedPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={approvedPaginator?.currentPage === 1}
                    className="p-2 rounded hover:bg-[#f5f5f5] disabled:opacity-50"
                    aria-label="Vorherige Seite"
                  >
                    <ChevronLeft className="size-5 text-[#6b6b6b]" />
                  </button>

                  {/* Desktop page numbers */}
                  <div className="hidden md:flex items-center gap-2">
                    {Array.from(
                      { length: approvedPaginator?.lastPage ?? 1 },
                      (_, i) => i + 1
                    )
                      .slice(
                        Math.max(
                          0,
                          (approvedPaginator?.currentPage ?? 1) - 3
                        ),
                        (approvedPaginator?.currentPage ?? 1) + 2
                      )
                      .map((page) => (
                        <button
                          key={page}
                          onClick={() => setApprovedPage(page)}
                          className={`px-3 py-1 rounded ${
                            approvedPaginator?.currentPage === page
                              ? "bg-[#0084c7] text-white"
                              : "text-[#6b6b6b] hover:bg-[#f5f5f5]"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                  </div>

                  {/* Mobile page indicator */}
                  <div className="md:hidden text-[13px] text-[#6b6b6b] min-w-[52px] text-center">
                    {(approvedPaginator?.currentPage ?? 1)} /{" "}
                    {(approvedPaginator?.lastPage ?? 1)}
                  </div>

                  {/* Next Arrow */}
                  <button
                    onClick={() =>
                      setApprovedPage((prev) =>
                        Math.min(
                          approvedPaginator?.lastPage ?? prev,
                          prev + 1
                        )
                      )
                    }
                    disabled={
                      approvedPaginator?.currentPage ===
                      approvedPaginator?.lastPage
                    }
                    className="p-2 rounded hover:bg-[#f5f5f5] disabled:opacity-50"
                    aria-label="Nächste Seite"
                  >
                    <ChevronRight className="size-5 text-[#6b6b6b]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eintrag löschen"
        message={
          <>
            <p>Möchten Sie den Eintrag wirklich löschen?</p>
            <div className="mt-3 space-y-1">
              <label className="text-[14px] text-[#6b6b6b]">
                Grund{' '}
                {isAdmin
                  ? '(optional)'
                  : <span className="text-red-500">*</span>
                }
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                placeholder="Bitte geben Sie einen Grund an..."
                className="w-full box-border px-3 py-2 border border-[#e0e0e0] rounded-lg text-[14px] text-[#1c1c1c] focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent resize-none"
              />
            </div>
          </>
        }
        onConfirm={() => {
          if (!isAdmin && !deleteReason.trim()) {
            toastError('Bitte geben Sie einen Grund an');
            return;
          }
          if (requestToDelete) {
            deleteRequest({
              id: requestToDelete.id,
              type: mapTrackingTypeToEnum(requestToDelete.type),
              reason: deleteReason,
            });
            setDeleteModalOpen(false);
          }
        }}
      />

      {/* Edit Modal */}
      {editModalEntry && (
        <EditRequestModal
          entry={editModalEntry}
          onClose={() => setEditModalEntry(null)}
        />
      )}

      {/* Footer */}
        <div className="text-center my-4">
          <p className="text-[#9ca3af] text-[12px]">
            © 2026 ENT-TRO GmbH by{' '}
            <a
              href="https://curvedesign.de"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-inherit"
            >
              CurveDesign GmbH
            </a>
          </p>
        </div>
    </div>
  );
}
